/**
 * `docCommands` (W1-10a; DESIGN.md §4.16, §5.6.7, §6.11, §6.12; model contract ChartMeta / PackageSettings).
 *
 *  - patchMeta keeps the song-select preview range valid (0 <= previewStart, previewEnd null or > previewStart:
 *    Phira panics on start > end) and refers only to existing assets. The difficulty (>= 0) and the RPE id (digits)
 *    are checked only when the patch changes them: imports keep META.id and the difficulty verbatim, and a rename
 *    of such a chart must not fail on a field it does not touch;
 *  - patchPkg keeps package settings in range (aspect > 0, dim 0..1, line length > 0, bake tolerance > 0, integer
 *    RPEVersion >= 0); editing rpeVersion drops the imported raw lexeme (shadow rule);
 *  - setSpeedMode switches Classic <-> RPE 1.7 speed curves. Classic -> 1.7: the easings Phira used to ignore on
 *    speed keys (src.ignoredEase) are dropped (rendering unchanged) or applied (they now take effect; never Bezier,
 *    Phira's 170 integrator panics on it). 1.7 -> Classic: eased segments stay and are baked on export.
 *    RPEVersion changes only when asked ('raise' writes 170 when it is lower), never automatically.
 * Assets, effects and videos: assets.ts and effects.ts.
 */

import { Ease as EaseApi, LINEAR } from '../ease/index';
import type { Ease } from '../ease/types';
import type { ChartMeta, IgnoredEase, Key, KeySrc, PackageSettings } from '../model/types';
import { assetCommands } from './assets';
import { effectCommands } from './effects';
import { assetOf, isFiniteNumber, type Patch, type Tx } from './structureKit';
import type { DocCommands } from './types';

/** RPEVersion from which Phira's 170 speed integrator runs in its modern sub-mode. */
export const RPE_VERSION_170 = 170;

const META_ASSET_FIELDS = ['music', 'illustration', 'unlockVideo'] as const;
const LINEAR_ID = LINEAR.kind === 'tween' ? LINEAR.id : 1;

const patchMeta: DocCommands['patchMeta'] = (tx: Tx, { patch }) => {
  const cur = tx.doc.meta;
  const clean: Patch<ChartMeta> = {};
  for (const [k, v] of Object.entries(patch)) if (v !== undefined) (clean as Record<string, unknown>)[k] = v;
  // `clean` holds no undefined members (filtered above), so the merge is a full ChartMeta.
  const next = { ...cur, ...clean } as ChartMeta;
  // The preview range is checked on the merged values (one end may be patched alone), whenever either end changes.
  if (clean.previewStart !== undefined || clean.previewEnd !== undefined) {
    if (!(isFiniteNumber(next.previewStart) && next.previewStart >= 0)) tx.fail({ key: 'cmd.meta.previewStart' });
    if (next.previewEnd !== null && !(isFiniteNumber(next.previewEnd) && next.previewEnd > next.previewStart)) {
      tx.fail({ key: 'cmd.meta.previewEnd' });
    }
  }
  // Fields the editor writes stricter than an import keeps them (an imported META.id may be a song id, an imported
  // difficulty may be negative): checked only when this patch changes them, so other edits still go through.
  const d = clean.difficulty;
  if (d !== undefined && d !== cur.difficulty && !(isFiniteNumber(d) && d >= 0))
    tx.fail({ key: 'cmd.meta.difficulty' });
  const id = clean.rpeId;
  if (id !== undefined && id !== cur.rpeId && !(typeof id === 'string' && /^\d*$/.test(id))) {
    tx.fail({ key: 'cmd.meta.rpeId' });
  }
  if (clean.tags !== undefined && !(Array.isArray(clean.tags) && clean.tags.every((t) => typeof t === 'string'))) {
    tx.fail({ key: 'cmd.meta.tags' });
  }
  for (const f of META_ASSET_FIELDS) {
    const a = next[f];
    if (a !== null && a !== cur[f]) assetOf(tx, a);
  }
  if (Object.keys(clean).length > 0) tx.updateSingleton('meta', clean);
};

function badPkg(tx: Tx, field: string): never {
  return tx.fail({ key: 'cmd.pkg.invalid', params: { field } });
}

const patchPkg: DocCommands['patchPkg'] = (tx: Tx, { patch }) => {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) if (v !== undefined && k !== 'speedMode') clean[k] = v;
  const p = clean as Partial<PackageSettings>;
  if (p.aspectRatio !== undefined && !(isFiniteNumber(p.aspectRatio) && p.aspectRatio > 0)) badPkg(tx, 'aspectRatio');
  if (
    p.backgroundDim !== undefined &&
    !(isFiniteNumber(p.backgroundDim) && p.backgroundDim >= 0 && p.backgroundDim <= 1)
  )
    badPkg(tx, 'backgroundDim');
  if (p.lineLength !== undefined && !(isFiniteNumber(p.lineLength) && p.lineLength > 0)) badPkg(tx, 'lineLength');
  if (p.speedBakeTolPx !== undefined && !(isFiniteNumber(p.speedBakeTolPx) && p.speedBakeTolPx > 0))
    badPkg(tx, 'speedBakeTolPx');
  if (p.rpeVersion !== undefined && !(Number.isSafeInteger(p.rpeVersion) && p.rpeVersion >= 0))
    badPkg(tx, 'rpeVersion');
  if (p.infoOffsetSec !== undefined && !isFiniteNumber(p.infoOffsetSec)) badPkg(tx, 'infoOffsetSec');
  tx.updateSingleton('pkg', p);
};

/** The ease an ignored legacy speed easing becomes when applied in RPE 1.7 mode (null: cannot be applied). */
export function appliedIgnoredEase(ig: IgnoredEase): Ease | null {
  // Phira's 170 integrator never takes a Bezier (it would panic): such an easing cannot be applied.
  if (ig.bezier !== 0) return null;
  const id = ig.easingType >= 1 && ig.easingType <= 29 ? Math.trunc(ig.easingType) : 1;
  let left = Math.min(1, Math.max(0, ig.easingLeft));
  let right = Math.min(1, Math.max(0, ig.easingRight));
  if (!(left < right)) {
    left = 0;
    right = 1;
  }
  if (!Number.isFinite(left) || !Number.isFinite(right)) return null;
  if (EaseApi.sliceIsDegenerate(id, left, right)) return null;
  const e: Ease = { kind: 'tween', id, left, right };
  return EaseApi.validateFor(e, 'speed170') === null ? e : null;
}

function isWholeLinear(e: Ease): boolean {
  return e.kind === 'tween' && e.id === LINEAR_ID && e.left === 0 && e.right === 1;
}

/** `src` without `ignoredEase` (undefined when nothing remains). */
function srcWithoutIgnored(src: KeySrc): KeySrc | undefined {
  const { ignoredEase: _drop, ...rest } = src;
  return Object.keys(rest).length > 0 ? rest : undefined;
}

/** Speed keys carrying an ignored legacy easing. */
function ignoredSpeedKeys(tx: Tx): Key[] {
  const out: Key[] = [];
  for (const t of tx.doc.tracks.values()) {
    if (t.owner.kind !== 'line' || t.owner.prop !== 'speed') continue;
    for (const id of tx.idx.keysByTrack(t.id)) {
      const k = tx.doc.keys.get(id);
      if (k?.src?.ignoredEase !== undefined) out.push(k);
    }
  }
  return out;
}

const setSpeedMode: DocCommands['setSpeedMode'] = (tx: Tx, { mode, ignoredEase, rpeVersion }) => {
  const pkg = tx.doc.pkg;
  if (mode === 'rpe170') {
    if (pkg.speedMode !== 'rpe170') {
      let dropped = 0;
      let applied = 0;
      let skipped = 0;
      for (const k of ignoredSpeedKeys(tx)) {
        const ig = k.src?.ignoredEase as IgnoredEase;
        const dropSrc = (): void => {
          tx.update('keys', k.id, { src: srcWithoutIgnored(k.src as KeySrc) });
        };
        if (ignoredEase === 'drop') {
          dropped++;
          dropSrc();
          continue;
        }
        const e = appliedIgnoredEase(ig);
        if (e === null) {
          skipped++;
          dropSrc();
        } else if (isWholeLinear(e) || !isWholeLinear(k.ease)) {
          // Nothing to apply: the easing was linear anyway, or the segment has been edited since the import.
          dropSrc();
        } else {
          tx.update('keys', k.id, { ease: e });
          applied++;
        }
      }
      tx.updateSingleton('pkg', { speedMode: 'rpe170' });
      if (dropped > 0) tx.note({ key: 'cmd.speed.ignoredDropped', params: { count: dropped } });
      if (applied > 0) tx.note({ key: 'cmd.speed.ignoredApplied', params: { count: applied } });
      if (skipped > 0) tx.note({ key: 'cmd.speed.ignoredSkipped', params: { count: skipped } });
    }
    if (rpeVersion === 'raise' && tx.doc.pkg.rpeVersion < RPE_VERSION_170) {
      tx.updateSingleton('pkg', { rpeVersion: RPE_VERSION_170 });
    }
    return;
  }
  if (pkg.speedMode !== 'legacy') tx.updateSingleton('pkg', { speedMode: 'legacy' });
};

const ignoreProblem: DocCommands['ignoreProblem'] = (tx: Tx, { key, ignore }) => {
  const k = key.trim();
  if (k.length === 0) tx.fail({ key: 'cmd.problems.badKey' });
  const list = tx.doc.editor.ignoredProblems;
  const has = list.includes(k);
  if (ignore && !has) tx.updateSingleton('editor', { ignoredProblems: [...list, k] });
  if (!ignore && has) tx.updateSingleton('editor', { ignoredProblems: list.filter((x) => x !== k) });
};

const recordExport: DocCommands['recordExport'] = (tx: Tx, { at, seq, sha256, fileName }) => {
  if (!isFiniteNumber(at) || !Number.isSafeInteger(seq) || !/^[0-9a-f]{64}$/.test(sha256) || fileName.length === 0) {
    tx.fail({ key: 'cmd.export.badRecord' });
  }
  tx.updateSingleton('editor', { lastExport: { at, seq, sha256, fileName } });
};

export const docCommands: DocCommands = {
  patchMeta,
  patchPkg,
  setSpeedMode,
  ...assetCommands,
  ...effectCommands,
  ignoreProblem,
  recordExport,
};
